import { useCallback, useEffect, useRef, useState } from "react";
import { ExternalLink, Monitor, RotateCw, Smartphone, Tablet } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { useAppBridge, withNonce } from "@/lib/appBridge";

const DEVICES = [
  { key: "desktop", label: "Desktop", width: 1440, icon: Monitor },
  { key: "tablet", label: "Tablet", width: 768, icon: Tablet },
  { key: "phone", label: "Phone", width: 375, icon: Smartphone },
];
const HEIGHT = 680;

// Callback ref: re-observes whenever the box element mounts/remounts, so scale tracks resizes and layout shifts.
const useWidth = () => {
  const [w, setW] = useState(0);
  const [h, setH] = useState(0);
  const ro = useRef(null);
  const ref = useCallback((el) => {
    ro.current?.disconnect();
    if (!el) return;
    setW(el.getBoundingClientRect().width);
    setH(el.getBoundingClientRect().height);
    ro.current = new ResizeObserver(([e]) => { setW(e.contentRect.width); setH(e.contentRect.height); });
    ro.current.observe(el);
  }, []);
  useEffect(() => () => ro.current?.disconnect(), []);
  return [ref, w, h];
};

export const DevicePreview = ({ url, pages, appId, fill = false }) => {
  const [device, setDevice] = useState(DEVICES[0]);
  const [page, setPage] = useState("index.html");
  const [reloadKey, setReloadKey] = useState(0);
  const [box, boxW, boxH] = useWidth();
  const frame = useRef(null);
  const nonce = useAppBridge(frame, appId);
  useEffect(() => setPage("index.html"), [url]);
  if (!url) return <div data-testid="studio-preview-empty" className={`grid ${fill ? "h-full" : "h-[520px]"} place-items-center rounded-2xl border border-dashed border-white/10 text-sm text-slate-500`}>Build the site to see a preview.</div>;
  const scale = boxW ? Math.min(1, boxW / device.width) : 1;
  const height = fill ? Math.max(320, boxH || HEIGHT) : HEIGHT;
  const src = withNonce(assetUrl(url.replace(/index\.html$/, "") + page), nonce);
  return (
    <div className={fill ? "flex h-full flex-col" : ""}>
      <div className="mb-2 flex flex-wrap items-center gap-2">
        {fill ? (
          <select value={page} onChange={(e) => setPage(e.target.value)} data-testid="studio-preview-page-select" className="rounded-full border border-white/10 bg-transparent px-3 py-1 text-xs text-slate-200 outline-none">
            {pages.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        ) : pages.map((f) => (
          <button key={f} data-testid={`studio-preview-page-${f}`} onClick={() => setPage(f)}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${f === page ? "border-cyan-400/60 text-cyan-200" : "border-white/10 text-slate-300 hover:border-cyan-400/50"}`}>{f}</button>
        ))}
        {fill && <button onClick={() => setReloadKey((k) => k + 1)} aria-label="Reload preview" data-testid="studio-preview-reload" className="rounded-full border border-white/10 p-1.5 text-slate-300 hover:text-white"><RotateCw className="h-3.5 w-3.5" /></button>}
        {fill && <a href={assetUrl(url.replace(/index\.html$/, "") + page)} target="_blank" rel="noopener noreferrer" aria-label="Open in new tab" data-testid="studio-preview-open" className="rounded-full border border-white/10 p-1.5 text-slate-300 hover:text-white"><ExternalLink className="h-3.5 w-3.5" /></a>}
        <span className="ml-auto flex items-center gap-1 rounded-full border border-white/10 p-1">
          {DEVICES.map((d) => (
            <button key={d.key} onClick={() => setDevice(d)} aria-label={d.label} title={`${d.label} · ${d.width}px`} data-testid={`studio-preview-${d.key}`}
              className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs transition-colors ${device.key === d.key ? "bg-cyan-400/15 text-cyan-200" : "text-slate-500 hover:text-slate-200"}`}>
              <d.icon className="h-3.5 w-3.5" /><span className="hidden sm:inline">{d.label}</span>
            </button>
          ))}
        </span>
      </div>
      <p data-testid="studio-preview-width" className="mb-2 font-mono-tech text-[11px] text-slate-500">{device.width}px{scale < 1 ? ` · shown at ${Math.round(scale * 100)}%` : ""}</p>
      <div ref={box} style={fill ? undefined : { height: HEIGHT }} className={`relative overflow-hidden rounded-2xl border border-white/10 bg-[#0B0F18] ${fill ? "min-h-[320px] flex-1" : ""}`}>
        <iframe key={reloadKey} ref={frame} title="Site preview" src={src} sandbox="allow-scripts allow-forms" data-testid="studio-preview-iframe" data-device-width={device.width}
          className="absolute top-0 border-0 bg-white"
          style={{ width: device.width, height: height / scale, transform: `scale(${scale})`, transformOrigin: "top left", left: Math.max(0, (boxW - device.width * scale) / 2) }} />
      </div>
    </div>
  );
};
